// Reminders (opomniki): one-tap voice notes or typed notes, due dates, the user's own order (drag
// and drop), done list, in-app alerts, and when done reminders are deleted (when the user chose
// automatic deletion in the settings).
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { AutoDeleteDoneNotesDays, DocumentSummary, NotesOrder } from '../../../shared/types';
import { parseDueDate } from '../../../shared/dueparse';
import {
  addAudioClip,
  addTextClip,
  createDocument,
  deleteDocument,
  getLatestDocument,
  retranscribeClip,
  updateClip,
  updateDocument,
} from '../lib/api';
import { formatDateTime, formatDue, formatDueShort, formatDuration } from '../lib/format';
import { useDocument, useDocuments, useFeatures, useOutboxStatus, useRecorder } from '../lib/hooks';
import { DUE_GROUP_LABEL, dropIndex, groupByDue, noteLabel, rankChanges, sortOpen, sortOpenNotes } from '../lib/notes';
import { discardRecording, type RecordingResult } from '../lib/recorder';
import { projectForText } from '../lib/projects';
import { navigate } from '../lib/router';
import { exportFileName, noteToIcs, saveFile } from '../export';
import { ClipAudio } from '../components/AudioPlayer';
import { stopPlayback } from '../components/PlayButton';
import { confirmDialog } from '../components/Dialog';
import { DueEditor } from '../components/DueEditor';
import { EditableText } from '../components/EditableText';
import { errorText, isAbortError, newId, truncate, useLatest, useNow, warnNotPersisted } from '../components/helpers';
import { Icon } from '../components/Icon';
import { LevelMeter } from '../components/LevelMeter';
import { RecordingKeptCard, useContentHidden } from '../components/ContentHidden';
import { Link, useLeaveGuard } from '../components/NavGuard';
import { ProjectChip, ProjectFilter, ProjectPicker } from '../components/ProjectPicker';
import { RecordButton } from '../components/RecordButton';
import { MutedNotice, RecorderAnnouncer, RecorderErrorBox, UnsupportedNotice } from '../components/RecorderNotice';
import { Chip, ClipStatusChip, SummaryChips } from '../components/StatusChip';
import { toast } from '../components/Toasts';
import { autoDeleteLabel, deletesInText, savePreferences, usePreferences } from './settings/preferences';

const MIN_NOTE_MS = 600;
/** Notes loaded at first and added by every "Pokaži več" (the server returns at most 200). */
const NOTES_PAGE = 50;
const NOTES_MAX = 200;

// ---------- one-shot auto start (home "Opomnik" card: one tap to recording) ----------

/** A tap asks for it; the notes page shown right after it starts recording. */
const AUTO_START_WINDOW_MS = 5_000;
let autoStartRequestedAt: number | null = null;

/**
 * The next mount of the notes page (within a few seconds) starts recording immediately. Only ever
 * called from a tap (the home page's card, the shortcut page's button) – never on navigation.
 */
export function requestNoteAutoStart(): void {
  autoStartRequestedAt = Date.now();
}

function consumeAutoStart(): boolean {
  const at = autoStartRequestedAt;
  autoStartRequestedAt = null;
  return at !== null && Date.now() - at <= AUTO_START_WINDOW_MS;
}

// ---------- ordering ----------

export { sortOpenNotes };

function isOverdue(n: Pick<DocumentSummary, 'done' | 'dueAt'>, now: Date): boolean {
  return !n.done && n.dueAt !== null && Date.parse(n.dueAt) < now.getTime();
}

/** The user's chosen order while signed out (open mode): kept for this app session only. */
let localOrder: NotesOrder = 'due';

// ---------- due alerts ----------

/** "<id>|<dueAt>" of alerts already shown in this app session (a changed due date re-arms). */
const alerted = new Set<string>();

/** Notifications shown without the service worker (desktop fallback), so they can be closed again. */
const pageNotifications = new Set<Notification>();

/**
 * The system notification of a due reminder (`tag`: the note's id) does not show its text: the
 * notification shade and the lock screen are seen by others too, also after a logout. The text is
 * only in the in-app toast.
 */
async function showSystemNotification(tag: string): Promise<void> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const title = 'Opomnik je zapadel';
  const options: NotificationOptions = { body: 'Odprite aplikacijo za podrobnosti.', tag, icon: '/icon-192.png' };
  try {
    // Android Chrome only allows notifications through the service worker.
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration) {
      await registration.showNotification(title, options);
      return;
    }
  } catch (err) {
    console.warn('[notes] service worker notification failed', err);
  }
  try {
    const notification = new Notification(title, options);
    pageNotifications.add(notification);
    notification.addEventListener('close', () => pageNotifications.delete(notification));
  } catch (err) {
    console.warn('[notes] notification failed', err);
  }
}

/**
 * Closes the due-reminder notifications (those of the service worker belong to every tab of the
 * app) when the session ends or the user logs out: nothing of them stays for the next person at
 * the device. The app shows no other notifications.
 */
export async function closeReminderNotifications(): Promise<void> {
  for (const notification of pageNotifications) notification.close();
  pageNotifications.clear();
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    for (const notification of (await registration?.getNotifications()) ?? []) notification.close();
  } catch (err) {
    console.warn('[notes] closing notifications failed', err);
  }
}

function notificationPermission(): NotificationPermission | 'unsupported' {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/**
 * Shows a toast (and, when allowed, a system notification without the text) when an open note
 * becomes due.
 */
function useDueAlerts(notes: DocumentSummary[] | null): void {
  const openedAt = useRef(Date.now());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!notes) return;
    const open = notes.filter((n) => !n.done && n.dueAt);
    const nowMs = Date.now();
    for (const n of open) {
      const t = Date.parse(n.dueAt!);
      const key = `${n.id}|${n.dueAt}`;
      // Only reminders that become due while the page is open (older ones are shown in red).
      if (t > openedAt.current && t <= nowMs && !alerted.has(key)) {
        alerted.add(key);
        const text = truncate(noteLabel(n), 120);
        toast(`Opomnik: ${text}`, {
          kind: 'warning',
          durationMs: 0,
          private: true,
          action: { label: 'Odpri', onClick: () => navigate({ name: 'notes', id: n.id }, { replace: true }) },
        });
        void showSystemNotification(n.id);
      }
    }
    const upcoming = open.map((n) => Date.parse(n.dueAt!)).filter((t) => t > nowMs);
    if (upcoming.length === 0) return;
    const delay = Math.min(Math.min(...upcoming) - nowMs + 250, 2_147_000_000);
    const timer = setTimeout(() => setTick((x) => x + 1), delay);
    return () => clearTimeout(timer);
  }, [notes, tick]);
}

// ---------- page ----------

interface NoteSession {
  docId: string;
  clipId: string;
}

const allowNotesHash = (hash: string) => hash === '#/opomniki' || hash.startsWith('#/opomniki/');

export function NotesPage({ expandedId }: { expandedId?: string }) {
  const rec = useRecorder('dictation');
  const hidden = useContentHidden();
  const [limit, setLimit] = useState(NOTES_PAGE);
  // '' = every project, 'none' = reminders without one (filtered here), else a project id (the server filters).
  const [projectFilter, setProjectFilter] = useState('');
  const features = useFeatures().data;
  const userId = features?.authenticated ? (features.user?.id ?? null) : null;
  const preferences = usePreferences(userId);
  // By due date, or the user's own order (a preference on the server; the phone's list follows it).
  const [orderOverride, setOrderOverride] = useState<NotesOrder | null>(null);
  const order: NotesOrder = orderOverride ?? (userId ? (preferences?.notesOrder ?? 'due') : localOrder);
  const { data: fresh, loading, error, stale, reload } = useDocuments({
    type: 'note',
    limit,
    project: projectFilter && projectFilter !== 'none' ? projectFilter : undefined,
    order,
  });
  // Keep the shorter list on screen while a longer one loads ("Pokaži več").
  const [previous, setPrevious] = useState<DocumentSummary[] | null>(null);
  useEffect(() => {
    if (fresh) setPrevious(fresh);
  }, [fresh]);
  const data = fresh ?? previous;
  const now = useNow(30_000);
  const [session, setSession] = useState<NoteSession | null>(null);
  const sessionRef = useRef<NoteSession | null>(null);
  const startPromise = useRef<Promise<void> | null>(null);
  const finishing = useRef<Promise<boolean> | null>(null);
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [permission, setPermission] = useState(notificationPermission);
  // Only signed-in users can choose it (reminders without an owner are never deleted).
  const autoDeleteDays = preferences?.autoDeleteDoneNotesDays ?? null;

  const chooseOrder = (next: NotesOrder) => {
    if (next === order) return;
    setOrderOverride(next);
    if (!userId) {
      localOrder = next;
      return;
    }
    savePreferences(userId, { notesOrder: next }).catch((err: unknown) => {
      // Kept for this visit; the server keeps the old one until it is reachable again.
      toast(errorText(err, 'Vrstnega reda ni bilo mogoče shraniti; velja le do zaprtja strani.'), { kind: 'warning' });
    });
  };

  // No reminder titles in system notifications while the login is shown.
  useDueAlerts(hidden ? null : data);

  const clearSession = () => {
    sessionRef.current = null;
    setSession(null);
  };

  const startRecording = () => {
    if (sessionRef.current || finishing.current || !rec.supported) return;
    // A note being played back would be recorded into the new one.
    stopPlayback();
    for (const el of document.querySelectorAll('audio')) el.pause();
    const info: NoteSession = { docId: newId(), clipId: newId() };
    sessionRef.current = info;
    setSession(info);
    const promise = rec.start({ docId: info.docId, clipId: info.clipId, type: 'note', title: '', seq: 1, diarize: false }).catch(() => {
      // rec.error holds the message (shown with a retry button).
      if (sessionRef.current === info) clearSession();
    });
    startPromise.current = promise;
    void promise.finally(() => {
      if (startPromise.current === promise) startPromise.current = null;
    });
  };
  const startRef = useLatest(startRecording);

  /** Queues the note; resolves false when its audio could only be kept in memory. */
  const saveNote = async (result: RecordingResult, info: NoteSession): Promise<boolean> => {
    await createDocument(info.docId, { type: 'note', createdAt: result.startedAt }, { isNew: true });
    const { persisted } = await addAudioClip(info.docId, {
      id: info.clipId,
      blob: result.blob,
      mimeType: result.mimeType,
      durationMs: result.durationMs,
      createdAt: result.startedAt,
      seq: 1,
      diarize: false,
    });
    // Kept only in memory, the upload holds on to the recovery copy until the server has the audio.
    if (persisted && result.recoveryId) {
      await discardRecording(result.recoveryId).catch((err: unknown) => console.warn('[notes] discard failed', err));
    }
    return persisted;
  };

  const finishRecording = (): Promise<boolean> => {
    if (finishing.current) return finishing.current;
    const info = sessionRef.current;
    if (!info) return Promise.resolve(true);
    setSaving(true);
    const promise = (async () => {
      // A quick second tap (or leaving the page) can come before the recorder has started.
      if (startPromise.current) await startPromise.current;
      if (sessionRef.current !== info) return true;
      let result: RecordingResult;
      try {
        result = await rec.stop();
      } catch (err) {
        toast(errorText(err, 'Snemanja ni bilo mogoče ustaviti.'), { kind: 'error' });
        return false;
      }
      if (result.durationMs < MIN_NOTE_MS || result.blob.size === 0) {
        if (result.recoveryId) await discardRecording(result.recoveryId).catch(() => {});
        toast('Posnetek je bil prekratek in ni shranjen.', { kind: 'warning' });
        return true;
      }
      try {
        const persisted = await saveNote(result, info);
        setHighlight(info.docId);
        if (persisted) toast('Opomnik je shranjen. Besedilo bo prepisano v nekaj trenutkih.', { kind: 'success' });
        else warnNotPersisted(result.recoveryId !== null ? 'recoverable' : 'lost');
        return true;
      } catch (err) {
        const kept = result.recoveryId !== null;
        toast(`${errorText(err, 'Opomnika ni bilo mogoče shraniti.')}${kept ? ' Posnetek bo ponujen za obnovitev.' : ''}`, {
          kind: 'error',
          durationMs: 0,
        });
        return kept;
      }
    })().finally(() => {
      finishing.current = null;
      clearSession();
      setSaving(false);
    });
    finishing.current = promise;
    return promise;
  };
  const finishRef = useLatest(finishRecording);

  useEffect(() => {
    if (!rec.interrupted || !sessionRef.current) return;
    void finishRef.current();
  }, [rec.interrupted, finishRef]);

  useLeaveGuard(session !== null, finishRecording, {
    message: 'Opomnik se še snema. Ga želite ustaviti, shraniti in zapustiti stran?',
    allowHash: allowNotesHash,
  });

  // One tap from the home page: start as soon as the page is shown. Deferred so React's
  // development double-mount does not start (and immediately release) a recording.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (consumeAutoStart()) startRef.current();
    }, 0);
    return () => clearTimeout(timer);
  }, [startRef]);

  const addTyped = async () => {
    const value = text.trim();
    if (!value) return;
    let dueAt: string | null = null;
    try {
      dueAt = parseDueDate(value, new Date())?.dueAt ?? null;
    } catch {
      // The server derives the due date as well; the local guess is only a convenience.
      dueAt = null;
    }
    const docId = newId();
    // The server derives the project as well; the local match shows it at once (and offline).
    const project = projectForText(value);
    try {
      await createDocument(
        docId,
        { type: 'note', ...(dueAt ? { dueAt } : {}), ...(project ? { projectId: project.projectId } : {}) },
        { isNew: true },
      );
      await addTextClip(docId, { id: newId(), text: value, createdAt: new Date().toISOString(), seq: 1 });
      setText('');
      setHighlight(docId);
      toast(dueAt ? `Opomnik je shranjen (rok ${formatDue(dueAt)}).` : 'Opomnik je shranjen.', { kind: 'success' });
    } catch (err) {
      toast(errorText(err, 'Opomnika ni bilo mogoče shraniti.'), { kind: 'error' });
    }
  };

  const enableNotifications = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'denied') toast('Obvestila so v brskalniku onemogočena. Vklopite jih v nastavitvah brskalnika.', { kind: 'warning' });
    } catch (err) {
      toast(errorText(err, 'Obvestil ni bilo mogoče vklopiti.'), { kind: 'error' });
    }
  };

  const notes = projectFilter === 'none' ? (data ?? []).filter((n) => !n.projectId) : (data ?? []);
  const open = sortOpen(
    notes.filter((n) => !n.done),
    order,
    now,
  );
  const done = notes.filter((n) => n.done).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const doneOpen = showDone || done.some((n) => n.id === expandedId);
  const expandedMissing = expandedId !== undefined && data !== null && !notes.some((n) => n.id === expandedId);
  const recording = session !== null;
  const starting = recording && rec.state === 'preparing';
  // The server returned as many as asked for: there may be more (done notes are listed last).
  const truncated = data !== null && data.length >= limit;
  const more = truncated ? '+' : '';

  // Under the login (the session ended): only a running recording; the typed text stays in memory.
  if (hidden) return <RecordingKeptCard recording={recording} elapsedMs={rec.elapsedMs} />;

  return (
    <div className="page page--notes">
      <RecorderAnnouncer state={rec.state} />
      <div className="doc-head">
        <Link to={{ name: 'home' }} className="back-link">
          <Icon name="chevronLeft" size={20} /> Začetek
        </Link>
        <h1 className="page-title">Opomniki</h1>
      </div>

      <section className="card note-capture" aria-label="Nov opomnik">
        {rec.supported ? (
          <>
            <RecordButton
              recording={recording}
              label={recording ? 'Ustavi in shrani' : 'Povej opomnik'}
              onClick={() => (recording ? void finishRecording() : startRecording())}
              busy={starting || saving}
              disabled={saving || starting}
            />
            {recording && !starting && (
              <div className="note-capture__live">
                <span className="timer timer--sm" role="timer">
                  {formatDuration(rec.elapsedMs)}
                </span>
                <LevelMeter level={rec.level} />
              </div>
            )}
            <MutedNotice muted={recording && rec.muted} />
            {rec.error && !recording && <RecorderErrorBox message={rec.error} onRetry={startRecording} />}
          </>
        ) : (
          <UnsupportedNotice alternative="Opomnik lahko napišete." />
        )}
        <form
          className="note-input"
          onSubmit={(e) => {
            e.preventDefault();
            void addTyped();
          }}
        >
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ali napišite opomnik …"
            aria-label="Besedilo opomnika"
            enterKeyHint="send"
            maxLength={2000}
            lang="sl"
          />
          <button type="submit" className="icon-btn icon-btn--primary" aria-label="Dodaj opomnik" disabled={!text.trim()}>
            <Icon name="send" />
          </button>
        </form>
        <p className="muted small">Rok se prepozna iz besedila, npr. »jutri ob desetih pokliči Marka«.</p>
      </section>

      {permission === 'default' && notes.some((n) => !n.done && n.dueAt) && (
        <div className="notice notice--info">
          <Icon name="bell" size={20} className="notice__icon" />
          <p className="notice__content">Ko je stran odprta, vas lahko opozori tudi sistemsko obvestilo.</p>
          <button type="button" className="btn btn--secondary btn--small" onClick={() => void enableNotifications()}>
            Vklopi obvestila
          </button>
        </div>
      )}

      {stale && <p className="muted small offline-hint">Brez povezave – prikazani so podatki, shranjeni v tej napravi.</p>}

      <div className="notes-toolbar">
        <ProjectFilter id="notes-project-filter" value={projectFilter} onChange={setProjectFilter} />
        <div className="order-toggle" role="group" aria-label="Vrstni red opomnikov">
          <button type="button" className={`order-toggle__btn${order === 'due' ? ' is-active' : ''}`} aria-pressed={order === 'due'} onClick={() => chooseOrder('due')}>
            <Icon name="clock" size={16} /> Po roku
          </button>
          <button
            type="button"
            className={`order-toggle__btn${order === 'manual' ? ' is-active' : ''}`}
            aria-pressed={order === 'manual'}
            onClick={() => chooseOrder('manual')}
          >
            <Icon name="list" size={16} /> Moj vrstni red
          </button>
        </div>
      </div>

      {expandedMissing && expandedId && (
        <ul className="notes">
          <LinkedNoteItem id={expandedId} now={now} autoDeleteDays={autoDeleteDays} />
        </ul>
      )}

      <section aria-labelledby="open-notes">
        <h2 id="open-notes" className="section-title">
          Odprti opomniki{' '}
          {open.length > 0 && (
            <span className="count">
              {open.length}
              {done.length === 0 ? more : ''}
            </span>
          )}
        </h2>
        {error && !data ? (
          <div className="notice notice--error" role="alert">
            <Icon name="alert" size={20} className="notice__icon" />
            <div className="notice__content">
              <p>{error}</p>
              <button type="button" className="btn btn--secondary btn--small" onClick={reload}>
                <Icon name="refresh" size={18} /> Poskusi znova
              </button>
            </div>
          </div>
        ) : !data ? (
          <p className="muted loading-line">
            <Icon name="spinner" size={18} /> Nalagam …
          </p>
        ) : open.length === 0 ? (
          <p className="empty">Ni odprtih opomnikov.</p>
        ) : order === 'manual' ? (
          <>
            <p className="muted small">Primite ročaj ≡ in povlecite opomnik na želeno mesto (ali ga s tipkama ↑ ↓ premaknite).</p>
            <OrderedNotes notes={open} expandedId={expandedId} now={now} highlight={highlight} autoDeleteDays={autoDeleteDays} />
          </>
        ) : (
          groupByDue(open, now).map((g) => (
            <div key={g.group} className="notes-group">
              <h3 className={`notes-group__title${g.group === 'overdue' ? ' is-overdue' : ''}`}>
                {DUE_GROUP_LABEL[g.group]} <span className="count">{g.notes.length}</span>
              </h3>
              <ul className="notes">
                {g.notes.map((n) => (
                  <NoteItem
                    key={n.id}
                    id={n.id}
                    summary={n}
                    expanded={n.id === expandedId}
                    now={now}
                    highlight={n.id === highlight}
                    autoDeleteDays={autoDeleteDays}
                  />
                ))}
              </ul>
            </div>
          ))
        )}
      </section>

      {done.length > 0 && (
        <section className="done-section">
          <button type="button" className="section-toggle" aria-expanded={doneOpen} onClick={() => setShowDone(!doneOpen)}>
            <Icon name={doneOpen ? 'chevronUp' : 'chevronDown'} size={20} />
            Opravljeno <span className="count">{`${done.length}${more}`}</span>
          </button>
          {doneOpen && autoDeleteDays !== null && (
            <p className="muted small" data-testid="auto-delete-hint">
              Opravljeni opomniki se samodejno izbrišejo {autoDeleteLabel(autoDeleteDays)} (spremenite v{' '}
              <Link to={{ name: 'settings' }}>nastavitvah</Link>).
            </p>
          )}
          {doneOpen && (
            <ul className="notes notes--done">
              {done.map((n) => (
                <NoteItem
                  key={n.id}
                  id={n.id}
                  summary={n}
                  expanded={n.id === expandedId}
                  now={now}
                  highlight={false}
                  autoDeleteDays={autoDeleteDays}
                />
              ))}
            </ul>
          )}
        </section>
      )}

      {truncated &&
        (limit < NOTES_MAX ? (
          <button
            type="button"
            className="btn btn--secondary load-more"
            onClick={() => setLimit((l) => Math.min(NOTES_MAX, l + NOTES_PAGE))}
            disabled={loading}
          >
            <Icon name={loading ? 'spinner' : 'chevronDown'} size={20} /> Pokaži več opomnikov
          </button>
        ) : (
          <p className="muted small">Prikazanih je {NOTES_MAX} opomnikov. Starejše poiščite z iskanjem na začetni strani.</p>
        ))}
    </div>
  );
}

/** A note opened by link that is not in the loaded list (e.g. an older one): load it on its own. */
function LinkedNoteItem({ id, now, autoDeleteDays }: { id: string; now: Date; autoDeleteDays: AutoDeleteDoneNotesDays | null }) {
  const { data } = useDocument(id);
  return (
    <NoteItem
      id={id}
      summary={data && data.type === 'note' ? data : null}
      expanded
      now={now}
      highlight={false}
      autoDeleteDays={autoDeleteDays}
    />
  );
}

// ---------- one note ----------

interface NoteItemProps {
  id: string;
  /** null while only the full document is known (opened by link). */
  summary: DocumentSummary | null;
  expanded: boolean;
  now: Date;
  highlight: boolean;
  /** The user's period for deleting done reminders (null: never). */
  autoDeleteDays: AutoDeleteDoneNotesDays | null;
  /** The drag handle (the user's own order). */
  handle?: React.ReactNode;
  /** This row is being dragged. */
  dragging?: boolean;
}

/**
 * The open notes in the user's own order, with a drag handle per row: pointer events (mouse and
 * touch alike) move the row live, the drop stores the new rank; the arrow keys on the handle move
 * it one place at a time (rankChanges in lib/notes.ts).
 */
function OrderedNotes({
  notes,
  expandedId,
  now,
  highlight,
  autoDeleteDays,
}: {
  notes: DocumentSummary[];
  expandedId: string | undefined;
  now: Date;
  highlight: string | null;
  autoDeleteDays: AutoDeleteDoneNotesDays | null;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  /** While a row is dragged: the ids in their live order and the dragged id. */
  const [drag, setDrag] = useState<{ id: string; order: string[] } | null>(null);
  const dragRef = useRef<{ id: string; from: number; order: string[]; pointerId: number } | null>(null);
  const shown = drag ? (drag.order.map((id) => notes.find((n) => n.id === id)).filter(Boolean) as DocumentSummary[]) : notes;

  const store = (from: number, to: number) => {
    for (const change of rankChanges(notes, from, to)) {
      updateDocument(change.id, { sortOrder: change.sortOrder }).catch((err: unknown) =>
        toast(errorText(err, 'Vrstnega reda ni bilo mogoče shraniti.'), { kind: 'error' }),
      );
    }
  };

  const middles = (): number[] =>
    [...(listRef.current?.querySelectorAll<HTMLElement>(':scope > li') ?? [])].map((el) => {
      const r = el.getBoundingClientRect();
      return r.top + r.height / 2;
    });

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>, id: string) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    const from = notes.findIndex((n) => n.id === id);
    if (from < 0) return;
    // Every later pointer event reaches the handle, wherever the finger goes (jsdom has no capture).
    if (typeof e.currentTarget.setPointerCapture === 'function') e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { id, from, order: notes.map((n) => n.id), pointerId: e.pointerId };
    setDrag({ id, order: notes.map((n) => n.id) });
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const current = d.order.indexOf(d.id);
    // The middles of the other rows, in the live order without the dragged one.
    const all = middles();
    const others = all.filter((_, i) => i !== current);
    const target = dropIndex(others, e.clientY);
    if (target === current) return;
    const next = d.order.filter((x) => x !== d.id);
    next.splice(target, 0, d.id);
    d.order = next;
    setDrag({ id: d.id, order: next });
  };

  const onPointerEnd = (e: ReactPointerEvent<HTMLButtonElement>, cancelled: boolean) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    dragRef.current = null;
    setDrag(null);
    if (cancelled) return;
    const to = d.order.indexOf(d.id);
    store(d.from, to);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, id: string) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const from = notes.findIndex((n) => n.id === id);
    const to = e.key === 'ArrowUp' ? from - 1 : from + 1;
    if (from < 0 || to < 0 || to >= notes.length) return;
    store(from, to);
    // Keep the focus on the moved row's handle after React re-renders the list.
    const handle = e.currentTarget;
    setTimeout(() => handle.focus(), 0);
  };

  return (
    <ul className={`notes notes--ordered${drag ? ' is-dragging' : ''}`} ref={listRef}>
      {shown.map((n, i) => (
        <NoteItem
          key={n.id}
          id={n.id}
          summary={n}
          expanded={n.id === expandedId}
          now={now}
          highlight={n.id === highlight}
          autoDeleteDays={autoDeleteDays}
          dragging={drag?.id === n.id}
          handle={
            <button
              type="button"
              className="note__grip"
              aria-label={`Premakni: ${truncate(noteLabel(n), 60)} (mesto ${i + 1} od ${shown.length}; tipki ↑ ↓)`}
              onPointerDown={(e) => onPointerDown(e, n.id)}
              onPointerMove={onPointerMove}
              onPointerUp={(e) => onPointerEnd(e, false)}
              onPointerCancel={(e) => onPointerEnd(e, true)}
              onKeyDown={(e) => onKeyDown(e, n.id)}
              data-testid="note-grip"
            >
              <Icon name="grip" size={20} />
            </button>
          }
        />
      ))}
    </ul>
  );
}

function NoteItem({ id, summary, expanded, now, highlight, autoDeleteDays, handle, dragging = false }: NoteItemProps) {
  const [editingDue, setEditingDue] = useState(false);
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if ((highlight || expanded) && ref.current) ref.current.scrollIntoView?.({ block: 'nearest' });
  }, [highlight, expanded]);

  const done = summary?.done ?? false;
  const dueAt = summary?.dueAt ?? null;
  const overdue = summary ? isOverdue(summary, now) : false;
  const text = summary ? noteLabel(summary) : 'Opomnik';
  const dueShort = dueAt ? formatDueShort(dueAt, now) : '';

  const toggleExpanded = () => navigate(expanded ? { name: 'notes' } : { name: 'notes', id }, { replace: true });

  return (
    <li
      ref={ref}
      className={`note${overdue ? ' note--overdue' : ''}${done ? ' note--done' : ''}${expanded ? ' is-expanded' : ''}${highlight ? ' is-new' : ''}${dragging ? ' is-dragging' : ''}`}
    >
      <div className="note__row">
        {handle}
        {summary && (
          <label className="check" title={done ? 'Označi kot neopravljeno' : 'Označi kot opravljeno'}>
            <input
              type="checkbox"
              checked={done}
              onChange={(e) => {
                updateDocument(id, { done: e.target.checked }).catch((err: unknown) =>
                  toast(errorText(err, 'Spremembe ni bilo mogoče shraniti.'), { kind: 'error' }),
                );
              }}
              aria-label={`${done ? 'Opravljeno' : 'Označi kot opravljeno'}: ${truncate(text, 60)}`}
            />
            <span className="check__box" aria-hidden="true">
              <Icon name="check" size={18} />
            </span>
          </label>
        )}
        <button type="button" className="note__main" aria-expanded={expanded} onClick={toggleExpanded}>
          <span className="note__text">{expanded ? text : truncate(text, 140)}</span>
          {summary && !expanded && (
            <span className="note__brief">
              {dueShort && !done && (
                <span className={`note__due${overdue ? ' is-overdue' : ''}`}>
                  <Icon name="clock" size={13} /> {overdue ? `zamuja · ${dueShort}` : dueShort}
                </span>
              )}
              <ProjectChip doc={summary} />
              <SummaryChips summary={summary} />
              {done && autoDeleteDays !== null && (
                <span className="muted small" data-testid="auto-delete-in">
                  {deletesInText(summary.doneAt, autoDeleteDays, now)}
                </span>
              )}
            </span>
          )}
          {summary && expanded && <span className="note__meta">Ustvarjeno {formatDateTime(summary.createdAt)}</span>}
        </button>
        <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={20} className="note__chev" />
      </div>
      {summary && expanded && (
        <div className="note__chips">
          <SummaryChips summary={summary} />
          {!done && (
            <button
              type="button"
              className={`chip chip--button ${dueAt ? (overdue ? 'chip--overdue' : 'chip--due') : 'chip--neutral'}`}
              onClick={() => setEditingDue((v) => !v)}
              aria-expanded={editingDue}
              aria-label={dueAt ? `Rok ${formatDue(dueAt, now)}${overdue ? ', zamujeno' : ''}. Spremeni rok.` : 'Dodaj rok'}
            >
              <Icon name="clock" size={14} /> {dueAt ? formatDue(dueAt, now) : 'Dodaj rok'}
            </button>
          )}
          <ProjectPicker
            doc={summary}
            readOnly={done}
            noun="opomnika"
            onChange={(projectId) => {
              updateDocument(id, { projectId }).catch((err: unknown) =>
                toast(errorText(err, 'Projekta ni bilo mogoče shraniti.'), { kind: 'error' }),
              );
            }}
          />
          {done && <Chip tone="done">Opravljeno</Chip>}
          {done && autoDeleteDays !== null && (
            <span className="muted small" data-testid="auto-delete-in">
              {deletesInText(summary.doneAt, autoDeleteDays, now)}
            </span>
          )}
        </div>
      )}
      {editingDue && (
        <DueEditor
          dueAt={dueAt}
          onClose={() => setEditingDue(false)}
          onSave={(value) => {
            updateDocument(id, { dueAt: value }).catch((err: unknown) =>
              toast(errorText(err, 'Roka ni bilo mogoče shraniti.'), { kind: 'error' }),
            );
          }}
        />
      )}
      {expanded && <NoteDetail id={id} />}
    </li>
  );
}

function NoteDetail({ id }: { id: string }) {
  const { data: doc, loading, error, reload } = useDocument(id);
  const online = useOutboxStatus().online;

  if (!doc) {
    if (loading) {
      return (
        <p className="muted loading-line note__detail">
          <Icon name="spinner" size={18} /> Nalagam …
        </p>
      );
    }
    return (
      <div className="note__detail">
        <p className="muted">{error ?? 'Opomnik ne obstaja več.'}</p>
        {error && (
          <button type="button" className="btn btn--secondary btn--small" onClick={reload}>
            Poskusi znova
          </button>
        )}
      </div>
    );
  }

  const addToCalendar = async () => {
    try {
      // With a text edit this tap just committed (the field saves on blur).
      const latest = await getLatestDocument(doc);
      const ics = noteToIcs(latest);
      if (!ics) {
        toast('Opomnik nima roka. Najprej nastavite rok.', { kind: 'warning' });
        return;
      }
      await saveFile(new Blob([ics], { type: 'text/calendar;charset=utf-8' }), exportFileName(latest, 'ics'));
    } catch (err) {
      if (!isAbortError(err)) toast(errorText(err, 'Datoteke za koledar ni bilo mogoče pripraviti.'), { kind: 'error' });
    }
  };

  const remove = async () => {
    const ok = await confirmDialog({
      title: 'Izbriši opomnik?',
      message: 'Opomnik in njegov posnetek bosta izbrisana. Tega ni mogoče razveljaviti.',
      confirmLabel: 'Izbriši',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteDocument(id);
      navigate({ name: 'notes' }, { replace: true });
    } catch (err) {
      toast(errorText(err, 'Opomnika ni bilo mogoče izbrisati.'), { kind: 'error' });
    }
  };

  return (
    <div className="note__detail">
      {doc.clips.length === 0 && <p className="muted small">Opomnik nima vsebine.</p>}
      {doc.clips.map((clip) => (
        <div key={clip.id} className="note__clip">
          {(clip.localPending || clip.status !== 'done') && (
            <p className="note__status">
              <ClipStatusChip clip={clip} />
              <span className="muted small">
                {clip.localPending
                  ? online
                    ? 'Pošiljam posnetek …'
                    : 'Posnetek je shranjen v napravi in bo poslan, ko bo povezava na voljo.'
                  : clip.status === 'error'
                    ? clip.error || 'Prepis ni uspel.'
                    : 'Besedilo bo kmalu na voljo.'}
              </span>
            </p>
          )}
          {(clip.status === 'done' || clip.text.trim() !== '') && (
            <EditableText
              value={clip.text}
              onSave={(t) =>
                updateClip(id, clip.id, { text: t }).catch((err: unknown) =>
                  toast(errorText(err, 'Popravka ni bilo mogoče shraniti.'), { kind: 'error' }),
                )
              }
              ariaLabel="Besedilo opomnika"
              placeholder="V posnetku ni bilo prepoznanega govora – vpišite besedilo."
            />
          )}
          {clip.status === 'error' && clip.hasAudio && !clip.localPending && (
            <button
              type="button"
              className="btn btn--secondary btn--small"
              onClick={() =>
                retranscribeClip(id, clip.id).catch((err: unknown) =>
                  toast(errorText(err, 'Ponovnega prepisa ni bilo mogoče začeti.'), { kind: 'error' }),
                )
              }
            >
              <Icon name="refresh" size={18} /> Poskusi znova
            </button>
          )}
          <ClipAudio clip={clip} />
        </div>
      ))}
      <div className="note__actions">
        <button type="button" className="btn btn--secondary btn--small" onClick={() => void addToCalendar()} disabled={!doc.dueAt}>
          <Icon name="calendar" size={18} /> Dodaj v koledar
        </button>
        <button type="button" className="btn btn--danger-ghost btn--small" onClick={() => void remove()}>
          <Icon name="trash" size={18} /> Izbriši
        </button>
      </div>
      {!doc.dueAt && <p className="muted small">Za vnos v koledar najprej nastavite rok.</p>}
    </div>
  );
}
